import { CreatorLinks } from './components/CreatorLinks';
import { CreatorInformationSections } from './staticContent/CreatorInformationSections';
import { Header } from './components/Header';
import { CaseStudies } from './components/CaseStudies';
import { IOSAppQrCodeModal } from './components/IosAppQrCodeModal';
import { Feedback } from './components/Feedback';
import { GoLiveModal } from './components/GoLiveModal';
import { LiveSuccessModal } from './components/LiveSuccessModal';
import {
  GuideChecklistPreview,
  GuideChecklistPreviewSkeleton,
} from './components/GuideChecklistPreview';
import { GuideHeader } from '@client/new/pages/creator/guide/components/GuideHeader';
import { InspirationStories } from './components/InspirationStories';

import { cardsSection, externalUrls } from './mappers';
import { OnboardingCard } from './components/OnboardingCard';
import { MigrationInfoBox } from './components/MigrationInfoBox';
import { LandingPagePreviewBanner } from './components/LandingPagePreviewBanner';
import { WebinarModal } from './components/WebinarModal';
import { trackEvent } from '@services/tracking';
import { HomeVM } from './home.viewModel';

import { Typography } from '@design-system/components/typography/typography';

export const HomeView = (p: HomeVM) => {
  const checklist = p.guideChecklistPreview.checklist;
  const shouldShowGuidePreview =
    !p.guideLoading && checklist !== null && (!p.isLive || p.hasStrategy);
  const shouldShowStoriesWithOnboarding = p.showOnboardingCards && !p.isLive;
  const visibleCards = p.isLive
    ? cardsSection.filter((card) => card.title !== 'New to Playbook?')
    : cardsSection;

  return (
    <div className="w-full flex flex-col gap-8 max-w-[1200px] mx-auto">
      <Header {...p.headline} />
      <MigrationInfoBox {...p.migrationInfoBox} />
      <LandingPagePreviewBanner {...p.landingPagePreviewBanner} />
      {(!p.isLive || p.hasStrategy) && p.guideLoading && (
        <GuideChecklistPreviewSkeleton />
      )}
      {p.isLive && <CreatorLinks {...p} />}
      {shouldShowGuidePreview && checklist && (
        <>
          {p.hasStrategy && (
            <GuideHeader
              strategy={p.guideHeader.strategy}
              completedChecklistsCount={p.guideHeader.completedChecklistsCount}
              totalChecklistsCount={p.guideHeader.totalChecklistsCount}
              totalEstimatedTime={p.guideHeader.totalEstimatedTime}
              isLive={p.isLive}
              canGoLive={p.guideHeader.canGoLive}
              onGoLive={p.guideHeader.onGoLive}
              onReview={p.guideHeader.onReviewStrategy}
            />
          )}
          <GuideChecklistPreview
            checklist={checklist}
            checklistNumber={p.guideChecklistPreview.checklistNumber}
            influencerId={p.guideChecklistPreview.influencerId}
            hasStrategy={p.hasStrategy}
            onTaskToggle={p.guideChecklistPreview.onTaskToggle}
            onOpenGuide={p.guideChecklistPreview.onOpenGuide}
            onStartStrategy={p.guideChecklistPreview.onStartStrategy}
            onOpenTaskModal={p.guideChecklistPreview.onOpenTaskModal}
          />
          {shouldShowStoriesWithOnboarding && (
            <InspirationStories creatorId={p.influencerId} />
          )}
        </>
      )}
      {!p.isLive && <CreatorLinks {...p} />}

      {shouldShowStoriesWithOnboarding && !shouldShowGuidePreview && (
        <InspirationStories creatorId={p.influencerId} />
      )}

      <Typography variant="headline" size="large" style={{ color: '#1A1A1A' }}>
        Join The FREE Playbook Creator Community
      </Typography>

      <div className="flex gap-4 md:flex-row flex-col">
        {visibleCards.map((card) => (
          <OnboardingCard
            key={card.title}
            title={card.title}
            subtitle={'subtitle' in card ? card.subtitle : undefined}
            description={card.description}
            image={card.image}
            link={card.link}
            isFullWidth={visibleCards.length === 1}
            onClick={() => {
              trackEvent({
                type: 'Onboarding Tile Tapped',
                payload: {
                  Title: card.title,
                  Source: p.isLive ? 'Post-Launch' : 'Pre-Launch',
                },
              });
            }}
          />
        ))}
      </div>

      <Feedback {...p.feedback} influencerId={p.influencerId} />
      <CreatorInformationSections isLive={p.isLive} />
      <IOSAppQrCodeModal
        isOpen={p.openedModal === 'QrCode'}
        onClose={() => p.dispatch('closeModal')}
      />
      <GoLiveModal {...p.goLiveModal} />
      <LiveSuccessModal {...p.liveSuccessModal} />
      <WebinarModal
        isOpen={p.openedModal === 'Webinar'}
        onClose={() => p.dispatch('closeModal')}
      />

      <CaseStudies />
    </div>
  );
};
